import React, { useState, useEffect } from 'react';
import { Navigate, Routes, Route } from 'react-router-dom';
import { loadingOverlay, scrollPage } from 'utils/dom';
import Storage from 'utils/storageManager';
import { loadingRedirect } from 'utils/redirect';
import { config } from 'utils/config';
import { initAnalytics } from 'utils/analytics';
import { i18n } from 'utils/i18n';
import { withRouter, OnUpdateWrapper } from 'utils/hoc';
import { LOCATIONS, RESERVATIONS, WINDOW_OBJECT_KEYS, SESSION_STORAGE } from 'constants';
import { usePrevious } from 'hooks/usePrevious';
import LocationFinder from 'components/LocationFinder';
import TaxesModal from 'components/modals/TaxesModal';
import GuaranteedResModal from 'components/modals/GuaranteedReservationModal';
import RemoveCouponModal from 'components/modals/RemoveCouponModal';
import RemoveProductCodeModal from 'components/modals/RemoveProductCodeModal';
import ConfirmCancelReservationModal from 'components/modals/ConfirmCancelReservationModal';
import EligibilityLossModal from 'components/modals/EligibilityLossModal';
import CouponDoesNotApplyModal from 'components/modals/CouponDoesNotApplyModal/CouponDoesNotApplyModal';
import Header from 'components/Header';
import SessionTimeoutModal from 'components/modals/SessionTimeoutModal';
import CurrencyConversionModal from 'components/modals/CurrencyConversionModal';
import CondensedFooter from 'components/CondensedFooter';
import cn from 'classnames';
import RemoveCIDModal from 'components/modals/RemoveCIDModal';
import EditVehicleModal from 'components/modals/EditVehicleModal';
import RemovePartnerRewardsModal from 'components/modals/RemovePartnerRewardsModal';
import VehicleSelect from './VehicleSelect';
import Extras from './Extras';
import Review from './Review';
import ReservationFlowBookingWidget from './ReservationFlowBookingWidget';
import { propTypes } from './reservationFlowPropTypes';
import ProgressBar from './ProgressBar';
import DiscardReservationModal from './SharedModals/DiscardReservationModal';
import ModifyResBanner from './ModifyResBanner';

const { RESFLOW_PATHS_CONFIG } = RESERVATIONS;

/**
  This is a ReservationFlow component
*/
//  ToDO: move state outside of Rez flow - to be handled with ticket putting modal state
//  in redux
const defaultState = {
  initialPickupLocation: null,
  showReturnLocation: false,
  locationName: null,
  locationId: null,
};

const ReservationFlow = (props) => {
  const {
    pickup_location,
    one_way_rental,
    return_location,
    router,
    hydrateUiReservationsState,
    isGmiReservationStateEmpty,
    showSoldOutNearbyLocations,
    restoreGMIUIToInitiateState,
    gboReservationExists,
    isTrueModify,
    setPartnerRewardProgram,
    setPartnerRewardProgramMemberId,
    initiateUIPartnerRewards,
    isFetchingSession,
    isCounterBypassIneligible,
    openCounterBypassIneligibleModal,
    isReservationFlowNotAllowed,
    getRentalTermsAndConditions,
    prepaySelected,
  } = props;

  // keep state separated by res flow section: location, Vehicle, etc.
  const [state, setState] = useState(defaultState);
  // Track previous isFetchingSession
  const prevIsFetchingSession = usePrevious(isFetchingSession);

  const initializeLocations = () => {
    // decides which location to show first in a one_way_rental situation
    let showReturnLocation = false;

    if (
      one_way_rental &&
      return_location?.type !== LOCATIONS.TYPE_BRANCH &&
      pickup_location?.type === LOCATIONS.TYPE_BRANCH
    ) {
      showReturnLocation = true;
    }

    setState((prev) => ({
      ...prev,
      enableChoosePickupCta: pickup_location?.type !== LOCATIONS.TYPE_BRANCH,
      initialPickupLocation: pickup_location,
      showReturnLocation,
    }));
  };

  useEffect(() => {
    if (pickup_location) {
      initializeLocations();
      // hydrate UI from GMA if the GBO res is missing, such as when we haven't chosen a branch yet
      hydrateUiReservationsState({ initiateFromGma: !gboReservationExists || isTrueModify });
    }
    // When user logs in and we have PRP data we check users profile PRP:
    if (initiateUIPartnerRewards) {
      setPartnerRewardProgram(initiateUIPartnerRewards);
      setPartnerRewardProgramMemberId(initiateUIPartnerRewards?.member_id);
    }
  }, []);

  // Handle loadingOverlay seperatly to avoid infinite loading
  useEffect(() => {
    // when entering res-flow at any view, show the overlay if we're fetching the session
    if (!prevIsFetchingSession && isFetchingSession) {
      loadingOverlay();
    }

    // if and when fetching ends, remove the overlay
    if (prevIsFetchingSession && !isFetchingSession) {
      loadingOverlay(false);
    }

    // Validate isReservationFlowNotAllowed only after current session was fetched
    if (prevIsFetchingSession !== isFetchingSession) {
      // If reservation flow pages are not allowed, should redirect to VMC details -- redirect to home page
      isReservationFlowNotAllowed && loadingRedirect(WINDOW_OBJECT_KEYS.HOME_PAGE_URL);
    }
  }, [isFetchingSession]);

  useEffect(() => {
    const initatedResFlowInTrueModify = Storage.SessionStorage.get(SESSION_STORAGE.INITIATE_RES_FLOW_TRUE_MODIFY);

    // initiate call was clearing gma and other car class details after we make changes to pickup date/time and pick/return location.
    // storing the boolean value when it is initiated and clearing the resflow immediatedly as sometimes it might create a odd situations.
    Storage.SessionStorage.remove(SESSION_STORAGE.INITIATE_RES_FLOW_TRUE_MODIFY);

    if (pickup_location || initatedResFlowInTrueModify) {
      initializeLocations();
      // hydrate UI from GMA if the GBO res is missing, such as when we haven't chosen a branch yet
      hydrateUiReservationsState({ initiateFromGma: !gboReservationExists || isTrueModify });
    }

    if (pickup_location && !isFetchingSession && gboReservationExists) {
      getRentalTermsAndConditions?.();
    }
  }, [pickup_location?.id, gboReservationExists]);

  useEffect(() => {
    if (isTrueModify && isCounterBypassIneligible) {
      openCounterBypassIneligibleModal();
    }
  }, [isCounterBypassIneligible]);

  useEffect(() => {
    // When user logs in and we have PRP data we check users profile PRP:
    if (initiateUIPartnerRewards) {
      setPartnerRewardProgram(initiateUIPartnerRewards);
      setPartnerRewardProgramMemberId(initiateUIPartnerRewards?.member_id);
    }
  }, [initiateUIPartnerRewards]);

  const toggleShowReturnLocation = () => {
    setState((prev) => ({
      ...prev,
      showReturnLocation: !prev.showReturnLocation,
    }));
    router.navigate(RESFLOW_PATHS_CONFIG.location[0].toLowerCase());
  };

  const handleRouteUpdate = (pathname) => {
    // Scroll user back to the top of the page when route changes
    if (!window.pageXOffset) {
      scrollPage();
    }
    const isAtStartOfReservationFlow = window.location.hash.indexOf(RESERVATIONS.RESFLOW_PATHS_CONFIG.start[0]) !== -1;
    //  We need this check, there was a bug going on where the handleRouteUpdate would fire when we do a non-hard refresh and it wouldn't go through spa.js
    if (isGmiReservationStateEmpty) {
      !isAtStartOfReservationFlow && location.assign(config.getRedirectUrl(WINDOW_OBJECT_KEYS.RESERVATION_INFLOW));
    } else {
      const locationSelectPage = RESERVATIONS.RESFLOW_PATHS_CONFIG.location[0];
      // Only not call this if it's on select location page where user is coming from in res with a sold out location ("See nearby locaitons")
      if (!(locationSelectPage === pathname && showSoldOutNearbyLocations)) {
        hydrateUiReservationsState();
      }
    }
    // restore  if GMA > UI state and user is on location select page , hydrate initiate state to match with GMA pickup locaiton.
    restoreGMIUIToInitiateState?.();
    // Dispatch the analytics ready event to indicate a "new page load" (since this is a SPA)
    if (pathname !== '/start') {
      // To prevent firing analytics "page view" event twice on redirected #/start page
      initAnalytics(config?.getPageInfo());
    }
  };

  const genericTitle = `${i18n('reservation_flow_reservation')} - ${i18n('reservation_flow_page_title')}`;

  // Adds text to loading-overlay when user select/update location selection
  const overlayText = (textValue) => {
    setTimeout(() => {
      const overlay = document.querySelector('.loading-overlay');
      if (overlay !== null) {
        overlay.innerHTML = `<p>${textValue}</p>`;
      }
    }, '100');
  };

  const renderLocationFinder = () => {
    const pathname = router?.location?.pathname;
    const showReturnLocation = pathname === RESFLOW_PATHS_CONFIG.location[1];
    return (
      <LocationFinder
        enableChoosePickupCta={state.enableChoosePickupCta}
        initialPickupLocation={state.initialPickupLocation}
        one_way_rental={one_way_rental}
        reservationFlow
        selectedLocation={showReturnLocation ? return_location : pickup_location}
        showReturnLocation={showReturnLocation}
        toggleShowReturnLocation={toggleShowReturnLocation}
        overlayText={overlayText}
        genericTitle={genericTitle}
      />
    );
  };

  const renderVehicleSelect = () => <VehicleSelect overlayText={overlayText} genericTitle={genericTitle} />;

  const renderExtras = () => <Extras genericTitle={genericTitle} />;

  const renderReview = () => <Review genericTitle={genericTitle} />;

  const renderStart = () => <ReservationFlowBookingWidget />;

  const isAuthorMode = config?.getIsAuthorModeEnabled();
  const pathname = router?.location?.pathname;

  return (
    <>
      <section
        className={cn('reservation-flow theme--dark-blue', {
          'on-author': isAuthorMode,
        })}
      >
        <Header showDiscountElements showAlamoInsidersIfEnabled />
        {isTrueModify && <ModifyResBanner />}
        <ProgressBar isTrueModify={isTrueModify} />
        <OnUpdateWrapper pathname={pathname} onUpdate={() => handleRouteUpdate(pathname)}>
          <Routes>
            <Route path={RESFLOW_PATHS_CONFIG.start[0]} exact element={renderStart()} />
            {RESFLOW_PATHS_CONFIG.location.map((path) => (
              <Route key={path} path={path} exact element={renderLocationFinder()} />
            ))}
            <Route path={RESFLOW_PATHS_CONFIG.vehicle[0]} exact element={renderVehicleSelect()} />
            <Route path={RESFLOW_PATHS_CONFIG.extras[0]} exact element={renderExtras()} />
            <Route path={RESFLOW_PATHS_CONFIG.review[0]} exact element={renderReview()} />
            <Route path='*' replace element={<Navigate to={RESFLOW_PATHS_CONFIG.start[0]} />} />
          </Routes>
        </OnUpdateWrapper>
        <CondensedFooter />
      </section>
      <EditVehicleModal />
      <RemoveCIDModal />
      <RemovePartnerRewardsModal />

      <TaxesModal />
      <CurrencyConversionModal />
      <DiscardReservationModal />
      <GuaranteedResModal />
      <RemoveCouponModal />
      <RemoveProductCodeModal />
      <ConfirmCancelReservationModal />
      {isTrueModify && <EligibilityLossModal />}
      {!prepaySelected && <CouponDoesNotApplyModal />}
      <SessionTimeoutModal />
    </>
  );
};

ReservationFlow.propTypes = propTypes;

export default withRouter(ReservationFlow);
